import React, { useState, useEffect } from 'react';
import { useSYS4110_R01Store } from './Store_SYS4110_R01';
import { CalendarDto, defaultCalendarDto } from 'src/dtos/calendar/calendarDto';
import { SingleDateForm } from 'src/components/common/form/SingleDateForm';
import { formatDate } from 'src/utils/string/stringUtil';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (event: CalendarDto) => void;
  onUpdate?: (event: CalendarDto) => void;
  onDelete?: (compCd: string, systemDay: string) => void;
  event?: CalendarDto | null;
}

// 폼 필드 컴포넌트
interface FormFieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ label, required = false, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && '*'}
    </label>
    {children}
  </div>
);

// 삭제 확인 모달 컴포넌트
interface DeleteConfirmModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
      <div className="bg-white p-6 rounded shadow-md">
        <p className="mb-4">정말 삭제하시겠습니까?</p>
        <div className="flex justify-end gap-2">
          <button 
            onClick={onCancel} 
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
          >
            취소
          </button>
          <button 
            onClick={onConfirm} 
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            삭제
          </button>
        </div>
      </div>
    </div>
  );
};

// 모달 헤더 컴포넌트
interface ModalHeaderProps {
  title: string;
  onClose: () => void;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ title, onClose }) => (
  <div className="flex items-center justify-between p-4 border-b">
    <h2 className="text-lg font-semibold">{title}</h2>
    <button
      onClick={onClose}
      className="text-gray-400 hover:text-gray-600 text-xl transition-colors"
    >
      ×
    </button>
  </div>
);

export const EventModal_SYS4110_R01: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUpdate,
  onDelete,
  event
}) => {
  const { selectedDate, selectedCompCd } = useSYS4110_R01Store();
  const { holidayTypeList } = useCommonCodeStore();
  const [formData, setFormData] = useState<CalendarDto>(defaultCalendarDto);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // SingleDateForm을 위한 상태 변수들
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [datePickerPos, setDatePickerPos] = useState({ top: 0, left: 0, height: 0 });

  useEffect(() => {
    if (isOpen) {
      if (event) {
        const holidayType = holidayTypeList.find(
          (item) => item.minorCd === event.holidayType || item.codeNm === event.holidayType
        )?.minorCd ?? event.holidayType;

        // 수정 모드: 기존 데이터 사용 - YYYY-MM-DD 형식으로 변환
        setFormData({
          ...event,
          holidayType,
          systemDay: event.systemDay.substring(0, 10)
        });
        setIsEditMode(true);
      } else {
        // 신규 등록 모드: 선택된 사업장 코드 자동 설정
        let initialDate = new Date();
        if (selectedDate) {
          initialDate = selectedDate;
        }
        const y = initialDate.getFullYear();
        const m = initialDate.getMonth() + 1;
        const d = initialDate.getDate();
        const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        
        setFormData({ 
          ...defaultCalendarDto, 
          systemDay: dateStr,  // YYYY-MM-DD 형식 (타임존 없음)
          compCd: selectedCompCd
        });
        setIsEditMode(false);
      }
    }
  }, [isOpen, event, selectedDate, selectedCompCd, holidayTypeList]);

  const handleInputChange = (field: keyof CalendarDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    if (!formData.compCd.trim()) {
      alert('사업장을 선택해주세요.');
      return false;
    }
    if (!formData.systemDay.trim()) {
      alert('날짜를 입력해주세요.');
      return false;
    }
    if (!formData.holidayName.trim()) {
      alert('휴일명을 입력해주세요.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (isEditMode && event && onUpdate) {
      onUpdate({ ...formData });
    } else {
      onSave({
        ...formData,
        compCd: selectedCompCd
      });
    }
    onClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (event && onDelete) {
      onDelete(event.compCd, event.systemDay);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <ModalHeader 
          title={isEditMode ? '휴일 수정' : '휴일 등록'} 
          onClose={onClose} 
        />

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <FormField label="날짜" required>
            <SingleDateForm
              title=""
              value={formData.systemDay ? new Date(formData.systemDay) : undefined}
              setValue={(date: Date | undefined) => handleInputChange('systemDay', formatDate(date))}
              editable={true}
              visible={datePickerVisible}
              setVisible={setDatePickerVisible}
              pickerPos={datePickerPos}
              setPickerPos={setDatePickerPos}
              titleless={true}
              wrapperOption={{
                wrapperStyle: "w-full min-w-0 flex-shrink-0",
                titleStyle: "w-0 min-w-0 hidden",
                inputStyle: "w-full min-w-0 flex-1"
              }}
            />
          </FormField>

          <FormField label="휴일명" required>
            <input
              type="text"
              value={formData.holidayName}
              onChange={(e) => handleInputChange('holidayName', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </FormField>

          <FormField label="휴일유형">
            <select
              value={formData.holidayType}
              onChange={(e) => handleInputChange('holidayType', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">선택</option>
              {holidayTypeList.map((holidayType) => (
                <option key={holidayType.minorCd} value={holidayType.minorCd}>
                  {holidayType.codeNm}
                </option>
              ))}
            </select>
          </FormField>

          {/* 비고 입력란은 제거 */}

          {/* <FormField label="휴일 여부 (Y/N)" required>
            <select
              value={formData.holidayYn}
              onChange={(e) => handleInputChange('holidayYn', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          </FormField> */}

          <div className="flex justify-end gap-2 pt-2">
            {isEditMode && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                삭제
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              저장
            </button>
          </div>
        </form>

        <DeleteConfirmModal
          isOpen={showDeleteConfirm}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      </div>
    </div>
  );
};
